import React from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Modal from "@/components/ui/modal";
import { Card, CardContent } from "@/components/ui/card";
import { ReactSelect } from "@/components/ui/react-select";
import { Badge } from "@/components/ui/badge";
import DefaultEditor from "react-simple-wysiwyg";
import { Box, Check, X, ScanBarcode, Layers, ChevronRight } from "lucide-react";

const ItemConfigModal = ({
  show,
  onHide,
  editingCartItem,
  selectedModalProduct,
  currencySymbol,
  currentItemConfig,
  setCurrentItemConfig,
  discountTypeOptions,
  availableBatches = [],
  calculateItemConfigTotal,
  handleSaveItemConfig,
  hideDiscountAndTax = false,
}) => {
  const trackingType = selectedModalProduct?.manage_inventory
    ? selectedModalProduct?.tracking_type ||
    (selectedModalProduct?.is_serialized ? "serial" : "none")
    : "none";

  const selectedBatch = availableBatches.find(
    (b) => b.id === currentItemConfig.inventory_batch_id,
  );

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(open) => !open && onHide()}
        title={editingCartItem ? "Edit Item" : "Add Item"}
        icon={Box}
        maxWidth="max-w-4xl"
        footer={
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground font-medium">
                Total Amount
              </span>
              <span className="text-2xl font-bold text-primary">
                {currencySymbol}
                {calculateItemConfigTotal().lineTotal.toFixed(2)}
              </span>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onHide} className="px-6">
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={handleSaveItemConfig} className="px-8 shadow-lg">
                <Check className="mr-2 h-4 w-4" />
                {editingCartItem ? "Update Item" : "Add to Cart"}
              </Button>
            </div>
          </div>
        }
      >
        {selectedModalProduct && (
          <div className="space-y-6 pb-10">
            {/* Product Info */}
            <Card className="bg-muted/50 border-none shadow-none">
              <CardContent className="p-3">
                <div className="flex items-center gap-4">
                  <img
                    src={
                      selectedModalProduct.media?.[0]?.url ||
                      "assets/img/product/noimage.png"
                    }
                    alt={selectedModalProduct.name}
                    className="rounded-md object-cover h-16 w-16 bg-background border"
                  />
                  <div className="space-y-1">
                    <h3 className="font-bold text-lg leading-none">
                      {selectedModalProduct.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      SKU: {selectedModalProduct.sku}
                    </p>
                    <p className="font-bold text-primary">
                      {currencySymbol}
                      {parseFloat(selectedModalProduct.sell_price).toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Quantity */}
              <div className="space-y-1">
                <Label className="font-bold text-xs uppercase tracking-tight text-muted-foreground">
                  Quantity
                </Label>
                <Input
                  type="number"
                  placeholder="Enter quantity"
                  disabled={trackingType === "serial" && !!editingCartItem}
                  value={currentItemConfig.quantity}
                  onChange={(e) => {
                    const quantity = parseInt(e.target.value) || 1;
                    setCurrentItemConfig({
                      ...currentItemConfig,
                      quantity,
                      serial_numbers:
                        currentItemConfig.serial_numbers?.slice(0, quantity) ||
                        [],
                    });
                  }}
                  min="1"
                  className="h-11"
                />
                {selectedModalProduct.manage_inventory && (
                  <p className="text-[10px] px-1 text-muted-foreground">
                    In Stock: {selectedModalProduct.sellable_qty}
                  </p>
                )}
              </div>

              {/* Tracking Info (Read-only during edit) */}
              {(currentItemConfig.inventory_batch_id ||
                (currentItemConfig.serial_numbers &&
                  currentItemConfig.serial_numbers.length > 0)) && (
                  <div className="space-y-1 col-span-full bg-muted/30 p-2 rounded-lg border border-dashed">
                    <Label className="font-bold text-[10px] uppercase tracking-tight text-muted-foreground flex items-center gap-1">
                      {trackingType === "standard" ? (
                        <>
                          <Layers className="h-3 w-3" /> Selected Batch
                        </>
                      ) : (
                        <>
                          <ScanBarcode className="h-3 w-3" /> Serial Numbers
                        </>
                      )}
                    </Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {trackingType === "standard" && selectedBatch && (
                        <Badge variant="secondary" className="font-mono py-0">
                          {selectedBatch.batch_number}
                        </Badge>
                      )}
                      {trackingType === "serial" &&
                        currentItemConfig.serial_numbers?.map((s) => (
                          <Badge
                            key={s}
                            variant="secondary"
                            className="font-mono text-[10px] py-0"
                          >
                            {s}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

              {/* Unit Price */}
              <div className="space-y-2">
                <Label className="font-bold">
                  Unit Price <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  placeholder="Enter unit price"
                  value={currentItemConfig.unit_price}
                  onChange={(e) =>
                    setCurrentItemConfig({
                      ...currentItemConfig,
                      unit_price: parseFloat(e.target.value) || 0,
                    })
                  }
                  min="0"
                  step="0.01"
                />
                <div className="flex gap-4">
                  {selectedModalProduct.sell_price_min && selectedModalProduct.sell_price_min > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Min: {currencySymbol}
                      {selectedModalProduct.sell_price_min}
                    </p>
                  )}
                  {selectedModalProduct.sell_price_max && selectedModalProduct.sell_price_max > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Max: {currencySymbol}
                      {selectedModalProduct.sell_price_max}
                    </p>
                  )}
                </div>
              </div>

              {/* Discount Type */}
              {!hideDiscountAndTax && (
                <>
                  <div className="space-y-2">
                    <Label className="font-bold text-sm">Discount Type</Label>
                    <ReactSelect
                      options={discountTypeOptions}
                      placeholder="Select Discount Type"
                      value={
                        discountTypeOptions?.find(
                          (opt) => opt.value === currentItemConfig.discount_type,
                        ) || null
                      }
                      onChange={(option) =>
                        setCurrentItemConfig({
                          ...currentItemConfig,
                          discount_type: option?.value || "none",
                          discount_value: 0,
                        })
                      }
                      isClearable
                    />
                  </div>

                  {/* Discount Value */}
                  <div className="space-y-2">
                    <Label className="font-bold">Discount Value</Label>
                    <Input
                      type="number"
                      placeholder="Enter discount value"
                      value={currentItemConfig.discount_value || ""}
                      onChange={(e) =>
                        setCurrentItemConfig({
                          ...currentItemConfig,
                          discount_value: parseFloat(e.target.value) || 0,
                        })
                      }
                      min="0"
                      step="0.01"
                      disabled={
                        !currentItemConfig.discount_type ||
                        currentItemConfig.discount_type === "none"
                      }
                    />
                  </div>

                  {/* Tax Info (Automated) */}
                  <div className="space-y-2">
                    <Label className="font-bold text-sm text-muted-foreground flex items-center gap-1">
                      Sales Tax
                      {selectedModalProduct?.is_taxable ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-4 px-1 text-green-600 border-green-200"
                        >
                          Taxable
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-4 px-1 text-muted-foreground border-muted"
                        >
                          Non-taxable
                        </Badge>
                      )}
                    </Label>
                    <Input
                      type="text"
                      className="bg-muted/50 font-medium"
                      value={
                        selectedModalProduct?.is_taxable
                          ? `Automated (${selectedModalProduct?.tax_method || "Standard"})`
                          : "No Tax Applied"
                      }
                      readOnly
                      disabled
                    />
                  </div>

                  {/* Tax Amount (Read-only) */}
                  <div className="space-y-2">
                    <Label className="font-bold">Tax Amount</Label>
                    <Input
                      type="text"
                      className="bg-muted font-mono"
                      value={`${currencySymbol}${calculateItemConfigTotal().taxAmount.toFixed(2)}`}
                      readOnly
                      disabled
                    />
                  </div>
                </>
              )}

              {/* Warranty Info */}
              <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-2">
                <div className="col-span-full flex justify-between items-center">
                  <Label className="font-bold text-sm flex items-center gap-2">
                    Warranty Information
                  </Label>
                  {selectedModalProduct?.warranty_timeframe &&
                    selectedModalProduct.warranty_timeframe !==
                    "No Warranty" && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] font-medium text-muted-foreground"
                      >
                        Product Default:{" "}
                        {selectedModalProduct.warranty_timeframe === "Lifetime"
                          ? "Lifetime"
                          : `${selectedModalProduct.warranty} ${selectedModalProduct.warranty_timeframe}`}
                      </Badge>
                    )}
                </div>
                <div className="space-y-2">
                  <Label className="font-bold">Start Date</Label>
                  <Input
                    type="date"
                    value={
                      currentItemConfig.warranty_start_date
                        ? currentItemConfig.warranty_start_date.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setCurrentItemConfig({
                        ...currentItemConfig,
                        warranty_start_date: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold">End Date</Label>
                  <Input
                    type="date"
                    value={
                      currentItemConfig.warranty_end_date
                        ? currentItemConfig.warranty_end_date.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setCurrentItemConfig({
                        ...currentItemConfig,
                        warranty_end_date: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      })
                    }
                  />
                </div>
                <div className="col-span-full space-y-2">
                  <Label className="font-bold">Warranty Notes</Label>
                  <div className="border rounded-md p-1 min-h-[120px]">
                    <DefaultEditor
                      value={currentItemConfig.warranty_notes || ""}
                      onChange={(e) =>
                        setCurrentItemConfig({
                          ...currentItemConfig,
                          warranty_notes: e.target.value,
                        })
                      }
                      style={{ height: "100px" }}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2 col-span-full border-t pt-4 mt-2">
                <Label className="font-bold">Notes</Label>
                <div className="border rounded-md p-1 min-h-[120px]">
                  <DefaultEditor
                    value={currentItemConfig.notes || ""}
                    onChange={(e) =>
                      setCurrentItemConfig({
                        ...currentItemConfig,
                        notes: e.target.value,
                      })
                    }
                    style={{ height: "100px" }}
                  />
                </div>
              </div>

              {/* Price Preview */}
              <Card className="col-span-full bg-primary/5 border-primary/20">
                <CardContent className="p-4 space-y-3 text-sm">
                  <h4 className="font-bold text-primary flex items-center gap-2 mb-2">
                    Price Breakdown
                  </h4>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Price:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {calculateItemConfigTotal().basePrice.toFixed(2)}
                    </span>
                  </div>
                  {!hideDiscountAndTax &&
                    calculateItemConfigTotal().discountAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount:</span>
                        <span className="text-destructive font-medium">
                          - {currencySymbol}
                          {calculateItemConfigTotal().discountAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  {!hideDiscountAndTax &&
                    calculateItemConfigTotal().taxAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span className="font-medium">
                          {currencySymbol}
                          {calculateItemConfigTotal().taxAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  <div className="pt-3 border-t flex justify-between">
                    <strong className="text-primary text-base">
                      Line Total:
                    </strong>
                    <strong className="text-primary text-lg">
                      {currencySymbol}
                      {calculateItemConfigTotal().lineTotal.toFixed(2)}
                    </strong>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

ItemConfigModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  editingCartItem: PropTypes.object,
  selectedModalProduct: PropTypes.object,
  currencySymbol: PropTypes.string,
  currentItemConfig: PropTypes.object.isRequired,
  setCurrentItemConfig: PropTypes.func.isRequired,
  discountTypeOptions: PropTypes.array,
  taxOptions: PropTypes.array,
  taxes: PropTypes.array,
  availableSerials: PropTypes.array,
  availableBatches: PropTypes.array,
  calculateItemConfigTotal: PropTypes.func.isRequired,
  handleSaveItemConfig: PropTypes.func.isRequired,
  hideDiscountAndTax: PropTypes.bool,
};

export default ItemConfigModal;
